import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import type {
  FollowUpChannel,
  FollowUpLog,
  LocalDateTime,
  ReminderChannel,
  SupervisorReminder,
  Task,
  Uuid,
} from '@/data/db';
import { assertSupervisorReminder, assertTask } from '@/data/invariants';
import { NotFoundError, bumpAudit, markDataMutation, newAudit } from './support';

export interface AddLogInput {
  taskId: Uuid;
  occurredAtLocal: LocalDateTime;
  channel: FollowUpChannel;
  summary: string;
  response?: string | null;
  contactIds?: Uuid[];
  nextFollowUpAtLocal?: LocalDateTime | null;
  supervisorReminderId?: Uuid | null;
}

export interface CreateReminderInput {
  taskId: Uuid;
  supervisorContactId: Uuid;
  remindAtLocal: LocalDateTime;
  message: string;
  channel: ReminderChannel;
}

/** spec 12.1 的五個選項。 */
export const FOLLOW_UP_CHOICES = [
  'awaiting_reply',
  'remind_again',
  'closed',
  'cancelled',
  'undecided',
] as const;

export type FollowUpChoice = (typeof FOLLOW_UP_CHOICES)[number];

export const FOLLOW_UP_CHOICE_LABELS: Record<FollowUpChoice, string> = {
  awaiting_reply: '還沒，等主管回覆',
  remind_again: '還沒，我要再提醒一次',
  closed: '結案了',
  cancelled: '不做了',
  undecided: '先不決定',
};

export type FollowUpResolution =
  | { choice: 'awaiting_reply'; nextFollowUpAtLocal?: LocalDateTime | null }
  | { choice: 'remind_again'; remindAtLocal: LocalDateTime }
  | { choice: 'closed' }
  | { choice: 'cancelled'; reason?: string }
  | { choice: 'undecided' };

export function createFollowUpRepository(db: GentleTaskDatabase = defaultDb) {
  const writeTables = [
    db.tasks,
    db.followUpLogs,
    db.supervisorReminders,
    db.boardColumns,
    db.boardPlacements,
    db.settings,
  ];

  async function requireTask(id: Uuid): Promise<Task> {
    const task = await db.tasks.get(id);
    if (!task) throw new NotFoundError('任務', id);
    return task;
  }

  async function requireReminder(id: Uuid): Promise<SupervisorReminder> {
    const reminder = await db.supervisorReminders.get(id);
    if (!reminder) throw new NotFoundError('主管提醒', id);
    return reminder;
  }

  async function completedQueueId(): Promise<Uuid> {
    const columns = await db.boardColumns.toArray();
    const queue = columns.find((column) => column.kind === 'completed_queue');
    if (!queue) throw new NotFoundError('完成佇列', 'completed_queue');
    return queue.id;
  }

  async function moveIntoQueue(taskId: Uuid, timestamp: string): Promise<void> {
    const queueId = await completedQueueId();
    const placement = await db.boardPlacements.where('taskId').equals(taskId).first();
    if (!placement || placement.columnId === queueId) return;

    const queued = await db.boardPlacements.where('columnId').equals(queueId).toArray();
    await db.boardPlacements.put({
      ...placement,
      columnId: queueId,
      order: queued.reduce((max, current) => Math.max(max, current.order), -1) + 1,
      columnIdBeforeCompletion: placement.columnId,
      ...bumpAudit(placement, timestamp),
    });
  }

  // ---------------------------------------------------------------- 追蹤紀錄

  async function addLog(input: AddLogInput): Promise<FollowUpLog> {
    const timestamp = nowUtc();
    const log: FollowUpLog = {
      id: newUuid(),
      taskId: input.taskId,
      occurredAtLocal: input.occurredAtLocal,
      contactIds: input.contactIds ?? [],
      channel: input.channel,
      summary: input.summary,
      response: input.response ?? null,
      nextFollowUpAtLocal: input.nextFollowUpAtLocal ?? null,
      supervisorReminderId: input.supervisorReminderId ?? null,
      deletedAt: null,
      ...newAudit(timestamp),
    };

    await db.transaction('rw', writeTables, async () => {
      await requireTask(input.taskId);
      await db.followUpLogs.add(log);
      await markDataMutation(db, timestamp);
    });

    return log;
  }

  async function updateLog(
    id: Uuid,
    patch: Partial<
      Pick<
        FollowUpLog,
        | 'occurredAtLocal'
        | 'channel'
        | 'summary'
        | 'response'
        | 'contactIds'
        | 'nextFollowUpAtLocal'
      >
    >,
  ): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.followUpLogs.get(id);
      if (!current) throw new NotFoundError('追蹤紀錄', id);
      await db.followUpLogs.put({ ...current, ...patch, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  /** 追蹤紀錄是歷史資料，刪除一律先進垃圾桶（spec 11）。 */
  async function softDeleteLog(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.followUpLogs.get(id);
      if (!current) throw new NotFoundError('追蹤紀錄', id);
      await db.followUpLogs.put({
        ...current,
        deletedAt: timestamp,
        ...bumpAudit(current, timestamp),
      });
      await markDataMutation(db, timestamp);
    });
  }

  async function restoreLog(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.followUpLogs.get(id);
      if (!current) throw new NotFoundError('追蹤紀錄', id);
      await db.followUpLogs.put({ ...current, deletedAt: null, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  async function listLogs(taskId: Uuid): Promise<FollowUpLog[]> {
    const logs = await db.followUpLogs.where('taskId').equals(taskId).toArray();
    return logs
      .filter((log) => log.deletedAt === null)
      .sort((a, b) => a.occurredAtLocal.localeCompare(b.occurredAtLocal));
  }

  // ---------------------------------------------------------------- 主管提醒

  async function createReminder(input: CreateReminderInput): Promise<SupervisorReminder> {
    const timestamp = nowUtc();
    const reminder: SupervisorReminder = {
      id: newUuid(),
      taskId: input.taskId,
      supervisorContactId: input.supervisorContactId,
      remindAtLocal: input.remindAtLocal,
      message: input.message,
      channel: input.channel,
      state: 'pending',
      snoozedUntilLocal: null,
      completedAt: null,
      followUpPromptDismissedAt: null,
      ...newAudit(timestamp),
    };

    await db.transaction('rw', writeTables, async () => {
      await requireTask(input.taskId);
      assertSupervisorReminder(reminder);
      await db.supervisorReminders.add(reminder);
      await markDataMutation(db, timestamp);
    });

    return reminder;
  }

  async function snoozeReminder(id: Uuid, until: LocalDateTime): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireReminder(id);
      const next: SupervisorReminder = {
        ...current,
        state: 'snoozed',
        snoozedUntilLocal: until,
        ...bumpAudit(current, timestamp),
      };
      assertSupervisorReminder(next);
      await db.supervisorReminders.put(next);
      await markDataMutation(db, timestamp);
    });
  }

  async function cancelReminder(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await requireReminder(id);
      const next: SupervisorReminder = {
        ...current,
        state: 'cancelled',
        snoozedUntilLocal: null,
        completedAt: null,
        ...bumpAudit(current, timestamp),
      };
      assertSupervisorReminder(next);
      await db.supervisorReminders.put(next);
      await markDataMutation(db, timestamp);
    });
  }

  async function listReminders(taskId: Uuid): Promise<SupervisorReminder[]> {
    const reminders = await db.supervisorReminders.where('taskId').equals(taskId).toArray();
    return reminders.sort((a, b) => a.remindAtLocal.localeCompare(b.remindAtLocal));
  }

  /**
   * 標示已提醒的完整交易（data-model 5.2）：
   * 1. state 改為 done、寫入 completedAt
   * 2. 建立追蹤紀錄，supervisorReminderId 指向本提醒
   * 3. 由呼叫端顯示「這件事結案了嗎」面板
   */
  async function markReminded(
    id: Uuid,
    occurredAtLocal: LocalDateTime,
  ): Promise<{ followUpLogId: Uuid }> {
    const timestamp = nowUtc();
    let logId!: Uuid;

    await db.transaction('rw', writeTables, async () => {
      const current = await requireReminder(id);
      const next: SupervisorReminder = {
        ...current,
        state: 'done',
        completedAt: timestamp,
        snoozedUntilLocal: null,
        ...bumpAudit(current, timestamp),
      };
      assertSupervisorReminder(next);
      await db.supervisorReminders.put(next);

      const log: FollowUpLog = {
        id: newUuid(),
        taskId: current.taskId,
        occurredAtLocal,
        contactIds: [current.supervisorContactId],
        channel: current.channel === 'email' ? 'email' : current.channel,
        summary: `已提醒主管：${current.message}`,
        response: null,
        nextFollowUpAtLocal: null,
        supervisorReminderId: current.id,
        deletedAt: null,
        ...newAudit(timestamp),
      };
      await db.followUpLogs.add(log);
      logId = log.id;

      await markDataMutation(db, timestamp);
    });

    return { followUpLogId: logId };
  }

  /** 面板已經問過就不再跳出（data-model 5.2 的 R3）。 */
  async function shouldAskFollowUp(reminderId: Uuid): Promise<boolean> {
    const reminder = await db.supervisorReminders.get(reminderId);
    if (!reminder) return false;
    return reminder.state === 'done' && reminder.followUpPromptDismissedAt === null;
  }

  /**
   * 任務已有 completedAt 或 cancelledAt 時，不顯示結案與不做了（spec 12.1）。
   * 「等主管回覆」也要一起拿掉：它會把 status 改成 waiting 卻不清 completedAt，
   * 直接違反 T2。已結束的任務要回到進行中只能走明確的「取消完成」。
   */
  async function availableFollowUpChoices(reminderId: Uuid): Promise<FollowUpChoice[]> {
    const reminder = await requireReminder(reminderId);
    const task = await requireTask(reminder.taskId);
    const finished = task.completedAt !== null || task.cancelledAt !== null;

    return FOLLOW_UP_CHOICES.filter((choice) => {
      if (!finished) return true;
      return choice !== 'closed' && choice !== 'cancelled' && choice !== 'awaiting_reply';
    });
  }

  /**
   * 面板選項的資料效果（spec 12.1）。
   * 每個選項都在單一交易中完成狀態、時間戳記與卡片移動，失敗時整筆還原。
   */
  async function resolveFollowUpPrompt(
    reminderId: Uuid,
    resolution: FollowUpResolution,
  ): Promise<SupervisorReminder | null> {
    const timestamp = nowUtc();
    let created: SupervisorReminder | null = null;

    await db.transaction('rw', writeTables, async () => {
      const reminder = await requireReminder(reminderId);
      const task = await requireTask(reminder.taskId);

      switch (resolution.choice) {
        case 'awaiting_reply': {
          // 已結束的任務不降級回 waiting（會違反 T2／T3），只記下後續處理。
          const finished = task.completedAt !== null || task.cancelledAt !== null;
          if (!finished) {
            const next: Task = {
              ...task,
              status: 'waiting',
              waitingSince: task.waitingSince ?? timestamp,
              ...bumpAudit(task, timestamp),
            };
            assertTask(next);
            await db.tasks.put(next);
          }

          if (resolution.nextFollowUpAtLocal) {
            const log = await db.followUpLogs
              .where('supervisorReminderId')
              .equals(reminderId)
              .last();
            if (log) {
              await db.followUpLogs.put({
                ...log,
                nextFollowUpAtLocal: resolution.nextFollowUpAtLocal,
                ...bumpAudit(log, timestamp),
              });
            }
          }
          break;
        }

        case 'remind_again': {
          // 預填相同主管、方式與提醒重點。
          const next: SupervisorReminder = {
            id: newUuid(),
            taskId: reminder.taskId,
            supervisorContactId: reminder.supervisorContactId,
            remindAtLocal: resolution.remindAtLocal,
            message: reminder.message,
            channel: reminder.channel,
            state: 'pending',
            snoozedUntilLocal: null,
            completedAt: null,
            followUpPromptDismissedAt: null,
            ...newAudit(timestamp),
          };
          assertSupervisorReminder(next);
          await db.supervisorReminders.add(next);
          created = next;
          break;
        }

        case 'closed': {
          const next: Task = {
            ...task,
            status: 'closed',
            completedAt: task.completedAt ?? timestamp,
            cancelledAt: null,
            cancelReason: null,
            waitingSince: null,
            ...bumpAudit(task, timestamp),
          };
          assertTask(next);
          await db.tasks.put(next);
          await moveIntoQueue(task.id, timestamp);
          break;
        }

        case 'cancelled': {
          const next: Task = {
            ...task,
            status: 'cancelled',
            cancelledAt: timestamp,
            cancelReason: resolution.reason ?? null,
            completedAt: null,
            waitingSince: null,
            ...bumpAudit(task, timestamp),
          };
          assertTask(next);
          await db.tasks.put(next);
          await moveIntoQueue(task.id, timestamp);
          break;
        }

        case 'undecided':
          // 只寫入 followUpPromptDismissedAt，不動其他任何欄位。
          break;
      }

      const dismissed = await requireReminder(reminderId);
      await db.supervisorReminders.put({
        ...dismissed,
        followUpPromptDismissedAt: timestamp,
        ...bumpAudit(dismissed, timestamp),
      });

      if (resolution.choice !== 'undecided') await markDataMutation(db, timestamp);
    });

    return created;
  }

  return {
    addLog,
    updateLog,
    softDeleteLog,
    restoreLog,
    listLogs,
    createReminder,
    snoozeReminder,
    cancelReminder,
    listReminders,
    markReminded,
    shouldAskFollowUp,
    availableFollowUpChoices,
    resolveFollowUpPrompt,
  };
}

export type FollowUpRepository = ReturnType<typeof createFollowUpRepository>;
export const followUpRepository = createFollowUpRepository();
